import {
  ApplicationConfig,
  inject,
  isDevMode,
  provideAppInitializer,
  provideZoneChangeDetection,
} from '@angular/core';
import { provideRouter, TitleStrategy, withComponentInputBinding, withInMemoryScrolling } from '@angular/router';
import { provideHttpClient, withInterceptors } from '@angular/common/http';
import { authInterceptor, provideAuth, LogLevel } from 'angular-auth-oidc-client';
import { provideTransloco } from '@jsverse/transloco';
import { provideTranslocoPersistLang } from '@jsverse/transloco-persist-lang';

import { routes } from './app.routes';
import { ENV_CONFIG, EnvConfig } from './core/env-config';
import { IconRegistry } from './shared/components/icon/icon-registry';
import { TranslocoHttpLoader } from './core/i18n/transloco-loader';
import { AVAILABLE_LANGS, DEFAULT_LANG, isSupportedLang } from './core/i18n/i18n.config';
import { TranslatedTitleStrategy } from './core/i18n/translated-title.strategy';
import { ContextService } from './core/context.service';

export function buildAppConfig(env: EnvConfig): ApplicationConfig {
    return {
        providers: [
            { provide: ENV_CONFIG, useValue: env },
            provideAuth({
                config: {
                    authority: `${env.oidcAuthority}/realms/${env.oidcRealm}`,
                    redirectUrl: env.oidcRedirectUri,
                    postLogoutRedirectUri: env.oidcPostLogoutRedirectUri,
                    clientId: env.oidcClientId,
                    scope: 'openid profile',
                    responseType: 'code',
                    silentRenew: true,
                    useRefreshToken: true,
                    logLevel: LogLevel.Debug,
                    strictIssuerValidationOnWellKnownRetrievalOff: false,
                    checkRedirectUrlWhenCheckingIfIsCallback: false,
                    secureRoutes: [env.coreApiUrl],
                    customParamsAuthRequest: {
                        audience: env.oidcClientId,
                    },
                    customParamsRefreshTokenRequest: {
                        scope: 'openid profile',
                    },
                },
            }),
            provideHttpClient(withInterceptors([authInterceptor()])),
            provideTransloco({
                config: {
                    availableLangs: AVAILABLE_LANGS.map(l => l.id),
                    defaultLang: DEFAULT_LANG,
                    fallbackLang: DEFAULT_LANG,
                    // Required so translated views re-render when the user switches language at runtime.
                    reRenderOnLangChange: true,
                    prodMode: !isDevMode(),
                    missingHandler: { logMissingKey: true, useFallbackTranslation: true },
                },
                loader: TranslocoHttpLoader,
            }),
            // Persists the active language to localStorage and restores it on return.
            // Initial resolution: cached choice -> browser language (if supported) -> default.
            provideTranslocoPersistLang({
                storage: { useValue: localStorage },
                storageKey: 'preferredLang',
                getLangFn: ({ cachedLang, browserLang, defaultLang }) =>
                    cachedLang || (isSupportedLang(browserLang) ? browserLang! : defaultLang),
            }),
            provideZoneChangeDetection({ eventCoalescing: true }),
            // Inject the icon sprite into the DOM before the app renders.
            provideAppInitializer(() => inject(IconRegistry).load()),
            provideAppInitializer(() => inject(ContextService).load()),
            provideRouter(
                routes,
                withInMemoryScrolling({ scrollPositionRestoration: 'enabled', anchorScrolling: 'enabled' }),
                withComponentInputBinding()
            ),
            // Route titles are Transloco keys — see TranslatedTitleStrategy.
            // useExisting (not useClass): the topbar injects the strategy
            // directly to render the page title, so the router must drive the
            // same root instance rather than a private copy.
            { provide: TitleStrategy, useExisting: TranslatedTitleStrategy },
        ],
    };
}
